import { useMemo, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import NewsCard from "@/components/feature/NewsCard";
import NewsModal from "@/components/feature/NewsModal";
import Pagination from "@/components/base/Pagination";
import { newsList, type NewsItem, type NewsType } from "@/mocks/news";

const PAGE_SIZE = 6;
const TYPE_OPTIONS: Array<NewsType | "全部"> = ["全部", "新闻", "活动", "访谈"];

export default function NewsPage() {
  const [type, setType] = useState<NewsType | "全部">("全部");
  const [sort, setSort] = useState<"new" | "old">("new");
  const [page, setPage] = useState(1);
  const [activeNews, setActiveNews] = useState<NewsItem | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  useSeo({
    title: "明星资讯 - 佐佐木明希官网",
    description:
      "佐佐木明希最新、最权威的明星资讯汇总，涵盖新作动态、公开活动与深度访谈，帮助你第一时间掌握她的每一步。",
    keywords: "佐佐木明希资讯,明星新闻,娱乐新闻,活动,访谈",
  });

  const filtered = useMemo(() => {
    const list = newsList.filter((item) => type === "全部" || item.type === type);
    return list.sort((a, b) =>
      sort === "new" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)
    );
  }, [type, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setType("全部");
    setSort("new");
    setPage(1);
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: "首页", path: "/" }, { label: "明星资讯" }]}
        title="明星资讯"
        lead="这里汇集佐佐木明希的全部明星资讯与娱乐新闻。可按资讯类型筛选、按时间排序，点击任意一条即可查看完整内容。"
      />

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
          <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
            <div className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 text-sm font-label font-medium text-foreground-700">
                <i className="ri-filter-3-line text-primary-600" aria-hidden="true" />
                筛选与排序
                <span className="text-foreground-400">共 {filtered.length} 条</span>
              </span>
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 md:hidden"
              >
                {showFilters ? "收起" : "展开"}
                <i className={showFilters ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="hidden items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-600 md:inline-flex"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置
              </button>
            </div>

            <div className={`${showFilters ? "block" : "hidden"} mt-4 space-y-4 md:mt-5 md:block`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <span className="w-16 shrink-0 text-sm text-foreground-500">类型</span>
                <div className="flex flex-wrap gap-2">
                  {TYPE_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        setType(option);
                        setPage(1);
                      }}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                        type === option
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50 text-foreground-700 hover:text-primary-600"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <span className="w-16 shrink-0 text-sm text-foreground-500">排序</span>
                <div className="inline-flex rounded-full bg-background-50 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setSort("new");
                      setPage(1);
                    }}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                      sort === "new" ? "bg-secondary-500 text-background-50" : "text-foreground-700"
                    }`}
                  >
                    最新
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSort("old");
                      setPage(1);
                    }}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-label transition-colors ${
                      sort === "old" ? "bg-secondary-500 text-background-50" : "text-foreground-700"
                    }`}
                  >
                    最旧
                  </button>
                </div>
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400" aria-hidden="true" />
              <p className="mt-4 font-heading text-lg text-foreground-800">暂无相关新闻</p>
              <p className="mt-2 text-sm text-foreground-500">换个筛选条件试试看</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
                {pageItems.map((item) => (
                  <NewsCard key={item.id} item={item} onOpen={setActiveNews} layout="row" />
                ))}
              </div>
              <div className="mt-10 flex flex-col items-center gap-4">
                <Pagination current={currentPage} total={totalPages} onChange={setPage} />
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-sm text-foreground-500 transition-colors hover:text-primary-600 md:hidden"
                >
                  <i className="ri-refresh-line" aria-hidden="true" />
                  重置筛选
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      <NewsModal item={activeNews} onClose={() => setActiveNews(null)} />
    </>
  );
}